<script lang="ts">
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { AqueductSession } from '$lib/aqueduct/engine/session';
	import { isWebGpuError } from '$lib/aqueduct/webGpuSupport';
	import type { ProjectHandle } from '$lib/aqueduct/engine/projectHandle';
	import type { SurfaceKind } from '$lib/aqueduct/engine/protocol';
	import { logLandingBoot, logLandingResourceSummary } from '$lib/landingBootLog';
	import {
		getDefaultMaterialGridSsaaScale,
		getMaterialViewportDpr,
		type MaterialGridSsaaScale
	} from '$lib/surfaceDpr';
	import LandingPoster from '$components/landing/LandingPoster.svelte';
	import WebGpuWarning from '$components/aqueduct/WebGpuWarning.svelte';
	import { DEFAULT_MATERIAL_DISPLACEMENT_SCALE } from '$lib/aqueduct/materialRendererDefaults';

	logLandingBoot('AqueductDemo: component script evaluated');

	const dispatch = createEventDispatcher<{
		ready: void;
		unavailable: void;
		failed: { error: string };
	}>();

	type AqueductLandingResources = {
		startedAt: number;
		graphToml: Promise<string>;
	};

	type LandingWindow = Window &
		typeof globalThis & {
			__aqueductLandingResources?: AqueductLandingResources;
		};

	type Status = 'idle' | 'loading' | 'ready' | 'error' | 'no-webgpu';
	type TunerTab = 'bloom' | 'edges' | 'particles';
	type BootDetails = Record<string, string | number | boolean | null | undefined>;
	type PrefetchResult = {
		graphToml: string;
	};

	type LandingDemoConfig = {
		bloomScale: number;
		bloomSigmaCssPx: number;
		bloomOpacity: number;
		edgeOpacity: number;
		edgeFalloff: number;
		edgeBrightness: number;
		particleSize: number;
		particleOuterOpacity: number;
		particleInnerOpacity: number;
		particleColorFalloff: number;
		particleGroupOpacity: number;
		displacementScale: number;
	};

	const BOOT_LOG_PREFIX = '[Aqueduct Landing Boot]';
	const DEMO_GRAPH_PATH = '/aqueduct/examples/demo/landing-page-demo-graph.aqueduct';
	const LANDING_TUNER_VISIBLE = false;
	const LANDING_BLOOM_SCALE = 1.0;
	const LANDING_BLOOM_SIGMA_CSS_PX = 4.0;
	const LANDING_BLOOM_OPACITY = 0.3;
	const LANDING_EDGE_OPACITY = 0.21;
	const LANDING_EDGE_FALLOFF = 2.0;
	const LANDING_EDGE_BRIGHTNESS = 4.0;
	const LANDING_PARTICLE_SIZE = 1.8;
	const LANDING_PARTICLE_OUTER_OPACITY = 0.85;
	const LANDING_PARTICLE_INNER_OPACITY = 0.75;
	const LANDING_PARTICLE_COLOR_FALLOFF = 2.5;
	const LANDING_PARTICLE_GROUP_OPACITY = 0.7;
	const LANDING_DISPLACEMENT_SCALE = DEFAULT_MATERIAL_DISPLACEMENT_SCALE;

	let bootStep = 0;
	let status = $state<Status>('idle');
	// The poster is the engine's own t=0 frame, captured by
	// scripts/capture-landing-posters.mjs and served statically. It replaces
	// the old wireframe skeleton: first paint shows the poster, and the live
	// canvas takes over only after its first frame (also t=0) has presented,
	// so the swap is pixel-continuous. It stays up when WebGPU is missing
	// (the no-webgpu overlay renders on top of it).
	let showPoster = $state(true);
	// ?poster: capture mode for the poster script; the worker renders one
	// frame pinned to t=0 and never starts the loop.
	let posterCaptureMode = $state(false);
	let containerEl: HTMLDivElement;
	let canvasEl: HTMLCanvasElement;
	let session: AqueductSession | null = null;
	let project = $state<ProjectHandle | null>(null);
	let tunerTab = $state<TunerTab>('bloom');
	let tunerExpanded = $state(true);
	let bloomSigmaCssPx = $state(LANDING_BLOOM_SIGMA_CSS_PX);
	let bloomOpacity = $state(LANDING_BLOOM_OPACITY);
	let edgeOpacity = $state(LANDING_EDGE_OPACITY);
	let edgeFalloff = $state(LANDING_EDGE_FALLOFF);
	let edgeBrightness = $state(LANDING_EDGE_BRIGHTNESS);
	let particleSize = $state(LANDING_PARTICLE_SIZE);
	let particleOuterOpacity = $state(LANDING_PARTICLE_OUTER_OPACITY);
	let particleInnerOpacity = $state(LANDING_PARTICLE_INNER_OPACITY);
	let particleColorFalloff = $state(LANDING_PARTICLE_COLOR_FALLOFF);
	let particleGroupOpacity = $state(LANDING_PARTICLE_GROUP_OPACITY);
	let resizeObserver: ResizeObserver | null = null;
	let themeObserver: MutationObserver | null = null;
	let unsubscribe: (() => void) | null = null;
	let isRegistered = false;
	let presentedHandled = false;
	let initStartedAt = 0;
	let lastCssWidth = 0;
	let lastCssHeight = 0;
	let lastDpr = 1;
	let lastMaterialGridSsaaScale: MaterialGridSsaaScale = 4;
	let prefetchPromise: Promise<PrefetchResult> | null = null;
	let destroyed: boolean = false;

	function formatMs(ms: number): string {
		return `${ms.toFixed(1)}ms`;
	}

	function logBoot(event: string, details: BootDetails = {}): void {
		const populatedDetails = Object.fromEntries(
			Object.entries(details).filter(([, value]) => value !== undefined)
		);
		console.log(
			`${BOOT_LOG_PREFIX} #${String(++bootStep).padStart(2, '0')} +${formatMs(performance.now())} ${event}`,
			populatedDetails
		);
	}

	function logBootDuration(event: string, startedAt: number, details: BootDetails = {}): void {
		logBoot(event, { duration: formatMs(performance.now() - startedAt), ...details });
	}

	async function fetchTextWithBootTiming(label: string, url: string): Promise<string> {
		const startedAt = performance.now();
		logBoot(`${label}: fetch start`, { url });
		const response = await fetch(url);
		const text = await response.text();
		logBootDuration(`${label}: fetch complete`, startedAt, {
			url,
			status: response.status,
			ok: response.ok,
			bytes: text.length
		});
		return text;
	}

	async function awaitEarlyTextWithBootTiming(
		label: string,
		promise: Promise<string>,
		fallbackUrl: string,
		earlyStartedAt: number
	): Promise<string> {
		const startedAt = performance.now();
		logBoot(`${label}: early promise await start`, {
			age: formatMs(startedAt - earlyStartedAt)
		});
		try {
			const text = await promise;
			logBootDuration(`${label}: early promise await complete`, startedAt, { bytes: text.length });
			return text;
		} catch (error) {
			logBootDuration(`${label}: early promise failed; falling back`, startedAt, {
				error: String(error)
			});
			return fetchTextWithBootTiming(label, fallbackUrl);
		}
	}

	function startPrefetch(): void {
		if (prefetchPromise) return;
		const startedAt = performance.now();
		const earlyResources = (window as LandingWindow).__aqueductLandingResources;
		logBoot('prefetch: start', { earlyResources: Boolean(earlyResources) });
		// Registry and node shaders are embedded in the wasm module; the demo
		// graph document is the only asset to prefetch.
		prefetchPromise = (
			earlyResources
				? awaitEarlyTextWithBootTiming(
						'demo graph',
						earlyResources.graphToml,
						DEMO_GRAPH_PATH,
						earlyResources.startedAt
					)
				: fetchTextWithBootTiming('demo graph', DEMO_GRAPH_PATH)
		).then((graphToml: string) => {
			logBootDuration('prefetch: complete', startedAt);
			return { graphToml };
		});
	}

	function currentDark(): boolean {
		return document.documentElement.classList.contains('dark');
	}

	type SurfaceMetrics = {
		cssWidth: number;
		cssHeight: number;
		dpr: number;
		materialGridSsaaScale: MaterialGridSsaaScale;
	};

	function surfaceMetrics(): SurfaceMetrics | null {
		if (!canvasEl || !containerEl) return null;
		const rect = containerEl.getBoundingClientRect();
		const dpr = getMaterialViewportDpr();
		return {
			cssWidth: rect.width,
			cssHeight: rect.height,
			dpr,
			materialGridSsaaScale: getDefaultMaterialGridSsaaScale(dpr)
		};
	}

	function logSurfaceSized(metrics: SurfaceMetrics): void {
		const width = Math.max(1, Math.round(metrics.cssWidth * metrics.dpr));
		const height = Math.max(1, Math.round(metrics.cssHeight * metrics.dpr));
		const bloomSigmaPx = bloomSigmaCssPx * metrics.dpr;
		const bloomRadiusPx = Math.ceil(bloomSigmaPx * 3);
		const bloomWidth = Math.max(1, Math.round(width * LANDING_BLOOM_SCALE));
		const bloomHeight = Math.max(1, Math.round(height * LANDING_BLOOM_SCALE));
		const bloomVerticalRadiusPx = Math.ceil(bloomSigmaPx * LANDING_BLOOM_SCALE * 3);
		const estimatedBloomSamples =
			bloomWidth * bloomHeight * (bloomRadiusPx * 2 + 1 + (bloomVerticalRadiusPx * 2 + 1));
		logBoot('landing surface sized', {
			cssWidth: Number(metrics.cssWidth.toFixed(2)),
			cssHeight: Number(metrics.cssHeight.toFixed(2)),
			backingWidth: width,
			backingHeight: height,
			selectedDpr: metrics.dpr,
			browserDpr: Number((window.devicePixelRatio || 1).toFixed(3)),
			coarsePointer: window.matchMedia('(pointer: coarse)').matches,
			sphereTargetPx: Math.max(1, Math.round(340 * metrics.dpr)),
			postprocessPx: width * height,
			bloomScale: LANDING_BLOOM_SCALE,
			bloomWidth,
			bloomHeight,
			bloomSigmaPx,
			bloomRadiusPx,
			bloomVerticalRadiusPx,
			estimatedBloomSamples,
			bloomSigmaCssPx,
			bloomOpacity,
			materialGridSsaaScale: metrics.materialGridSsaaScale
		});
	}

	function landingDemoConfig(): LandingDemoConfig {
		return {
			bloomScale: LANDING_BLOOM_SCALE,
			bloomSigmaCssPx,
			bloomOpacity,
			edgeOpacity,
			edgeFalloff,
			edgeBrightness,
			particleSize,
			particleOuterOpacity,
			particleInnerOpacity,
			particleColorFalloff,
			particleGroupOpacity,
			displacementScale: LANDING_DISPLACEMENT_SCALE
		};
	}

	function applyLandingConfig(targetProject: ProjectHandle | null = project): void {
		targetProject?.demoConfig('landingDemo', JSON.stringify(landingDemoConfig()));
	}

	function clampNumber(value: number, min: number, max: number): number | null {
		if (!Number.isFinite(value)) return null;
		return Math.min(max, Math.max(min, value));
	}

	function numberFromInput(event: Event): number | null {
		const input = event.currentTarget as HTMLInputElement | null;
		if (!input) return null;
		const value = input.valueAsNumber;
		return Number.isFinite(value) ? value : null;
	}

	function setBloomSigmaCssPx(value: number): void {
		const nextValue = clampNumber(value, 0, 16);
		if (nextValue === null) return;
		bloomSigmaCssPx = nextValue;
		applyLandingConfig();
	}

	function setBloomOpacity(value: number): void {
		const nextValue = clampNumber(value, 0, 1);
		if (nextValue === null) return;
		bloomOpacity = nextValue;
		applyLandingConfig();
	}

	function handleBloomSigmaInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setBloomSigmaCssPx(value);
	}

	function handleBloomOpacityInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setBloomOpacity(value);
	}

	function setEdgeOpacity(value: number): void {
		const nextValue = clampNumber(value, 0, 1);
		if (nextValue === null) return;
		edgeOpacity = nextValue;
		applyLandingConfig();
	}

	function setEdgeFalloff(value: number): void {
		const nextValue = clampNumber(value, 0.05, 8);
		if (nextValue === null) return;
		edgeFalloff = nextValue;
		applyLandingConfig();
	}

	function setEdgeBrightness(value: number): void {
		const nextValue = clampNumber(value, 0, 4);
		if (nextValue === null) return;
		edgeBrightness = nextValue;
		applyLandingConfig();
	}

	function handleEdgeOpacityInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setEdgeOpacity(value);
	}

	function handleEdgeFalloffInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setEdgeFalloff(value);
	}

	function handleEdgeBrightnessInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setEdgeBrightness(value);
	}

	function setParticleSize(value: number): void {
		const nextValue = clampNumber(value, 0.1, 4);
		if (nextValue === null) return;
		particleSize = nextValue;
		applyLandingConfig();
	}

	function setParticleOuterOpacity(value: number): void {
		const nextValue = clampNumber(value, 0, 4);
		if (nextValue === null) return;
		particleOuterOpacity = nextValue;
		applyLandingConfig();
	}

	function setParticleInnerOpacity(value: number): void {
		const nextValue = clampNumber(value, 0, 4);
		if (nextValue === null) return;
		particleInnerOpacity = nextValue;
		applyLandingConfig();
	}

	function setParticleColorFalloff(value: number): void {
		const nextValue = clampNumber(value, 0.1, 8);
		if (nextValue === null) return;
		particleColorFalloff = nextValue;
		applyLandingConfig();
	}

	function setParticleGroupOpacity(value: number): void {
		const nextValue = clampNumber(value, 0, 2);
		if (nextValue === null) return;
		particleGroupOpacity = nextValue;
		applyLandingConfig();
	}

	function handleParticleSizeInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setParticleSize(value);
	}

	function handleParticleOuterOpacityInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setParticleOuterOpacity(value);
	}

	function handleParticleInnerOpacityInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setParticleInnerOpacity(value);
	}

	function handleParticleColorFalloffInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setParticleColorFalloff(value);
	}

	function handleParticleGroupOpacityInput(event: Event): void {
		const value = numberFromInput(event);
		if (value !== null) setParticleGroupOpacity(value);
	}

	function resetTunerConfig(): void {
		if (tunerTab === 'bloom') {
			bloomSigmaCssPx = LANDING_BLOOM_SIGMA_CSS_PX;
			bloomOpacity = LANDING_BLOOM_OPACITY;
			applyLandingConfig();
		} else if (tunerTab === 'edges') {
			edgeOpacity = LANDING_EDGE_OPACITY;
			edgeFalloff = LANDING_EDGE_FALLOFF;
			edgeBrightness = LANDING_EDGE_BRIGHTNESS;
			applyLandingConfig();
		} else {
			particleSize = LANDING_PARTICLE_SIZE;
			particleOuterOpacity = LANDING_PARTICLE_OUTER_OPACITY;
			particleInnerOpacity = LANDING_PARTICLE_INNER_OPACITY;
			particleColorFalloff = LANDING_PARTICLE_COLOR_FALLOFF;
			particleGroupOpacity = LANDING_PARTICLE_GROUP_OPACITY;
			applyLandingConfig();
		}
	}

	function stopTunerPointer(event: PointerEvent): void {
		event.stopPropagation();
	}

	function canvasPoint(event: PointerEvent): { x: number; y: number } {
		const rect = canvasEl.getBoundingClientRect();
		return {
			x: event.clientX - rect.left,
			y: event.clientY - rect.top
		};
	}

	// Interactivity begins at release (demoStart): before that, frames are
	// held and forwarded pointer events would bank invisible simulation
	// state that bursts out all at once when rendering resumes.
	let interactive = false;

	function handlePointerDown(event: PointerEvent): void {
		if (!project || !event.isPrimary || !interactive) return;
		event.preventDefault();
		const point = canvasPoint(event);
		project.demoPointer('landingDemo', 0, point.x, point.y);
		canvasEl.setPointerCapture(event.pointerId);
	}

	function handlePointerMove(event: PointerEvent): void {
		if (!project || !event.isPrimary || !interactive) return;
		const point = canvasPoint(event);
		project.demoPointer('landingDemo', 1, point.x, point.y);
	}

	function handlePointerLeave(event: PointerEvent): void {
		if (!project || !event.isPrimary || !interactive) return;
		project.demoPointer('landingDemo', 1, -1, -1);
	}

	function handlePointerUp(event: PointerEvent): void {
		if (!project || !event.isPrimary || !interactive) return;
		if (canvasEl.hasPointerCapture(event.pointerId))
			canvasEl.releasePointerCapture(event.pointerId);
		const point = canvasPoint(event);
		project.demoPointer('landingDemo', 2, point.x, point.y);
	}

	function handleWindowPointerDown(event: PointerEvent): void {
		if (!project || !containerEl || !event.isPrimary) return;
		if (event.composedPath().includes(containerEl)) return;
		project.cmd('landingDemoClearSelection');
	}

	function handleInitError(error: unknown): void {
		if (destroyed) return;
		const message = String(error);
		if (isWebGpuError(error)) {
			status = 'no-webgpu';
			dispatch('unavailable');
		} else {
			status = 'error';
			console.error('AqueductDemo init failed:', error);
			dispatch('failed', { error: message });
		}
		logBootDuration('initEngine: failed', initStartedAt || performance.now(), { error: message });
	}

	function handlePresented(kinds: SurfaceKind[]): void {
		if (destroyed || presentedHandled || !kinds.includes('landingDemo')) return;
		presentedHandled = true;
		if (posterCaptureMode) {
			// Poster held frame is on screen; signal the capture script after
			// the compositor has shown it (double rAF).
			showPoster = false;
			requestAnimationFrame(() =>
				requestAnimationFrame(() => {
					(window as Window & { __landingPosterReady?: boolean }).__landingPosterReady = true;
				})
			);
			return;
		}
		logBootDuration('initEngine: first frame presented', initStartedAt);
		dispatch('ready');
		// Atomic swap, after the first canvas frame has actually presented
		// (double rAF): the canvas's CSS opacity is gated on !showPoster, so
		// the poster leaves and the canvas appears in the same frame. The
		// two layers never overlap; overlapping them double-composites
		// every semi-transparent pixel and reads as a flash.
		requestAnimationFrame(() =>
			requestAnimationFrame(() => {
				showPoster = false;
				// The worker holds a single t=0 frame (the poster pose) until
				// released; double rAF so the swap has composited before the
				// clock starts, and the worker stamps the origin at its next
				// tick so the first moving frame is exactly t=0.
				requestAnimationFrame(() =>
					requestAnimationFrame(() => {
						if (destroyed) return;
						interactive = true;
						project?.demoStart();
					})
				);
			})
		);
	}

	async function initEngine(): Promise<void> {
		initStartedAt = performance.now();
		logBoot('initEngine: start');
		if (!navigator.gpu) {
			status = 'no-webgpu';
			dispatch('unavailable');
			return;
		}

		try {
			status = 'loading';
			startPrefetch();
			session = new AqueductSession({
				docId: 'landing-demo',
				clientId: 'landing-demo',
				warmSlangCompiler: false
			});
			// Engine boot failures (worker, wasm, GPU init) surface here; the
			// per-call commands below are fire-and-forget and cannot report them.
			session.booted.catch((error) => handleInitError(error));
			// Synchronous: commands buffer FIFO behind engine boot in the worker,
			// so no boot-ordering ceremony is needed on this thread.
			const handle = session.project;
			project = handle;
			unsubscribe = session.subscribe({
				onPresented: handlePresented,
				onRenderError: (message: string) => {
					status = 'error';
					console.error('Aqueduct landing demo render failed:', message);
					dispatch('failed', { error: message });
				}
			});
			logBoot('engine: project opened (worker)');
			const { graphToml } = await prefetchPromise!;
			if (destroyed) return;
			// Poster capture is the exact t=0 frame: the animated graph must also
			// evaluate at t=0, independent of how long the page took to load.
			if (posterCaptureMode) handle.setTimeOverride(0);
			session.loadGraphToml(graphToml);
			// ?profile: per-frame stage timings in the console.
			if (new URLSearchParams(window.location.search).has('profile')) {
				handle.setLogConfig(JSON.stringify({ landingFrameProfiling: true }));
			}

			const metrics = surfaceMetrics();
			if (!metrics) throw new Error('Landing demo canvas could not be sized');
			lastCssWidth = metrics.cssWidth;
			lastCssHeight = metrics.cssHeight;
			lastDpr = metrics.dpr;
			lastMaterialGridSsaaScale = metrics.materialGridSsaaScale;
			await handle.prepareStartupPipelines('landingDemo');
			if (destroyed) return;
			// Backing store is sized before the transfer; afterwards only the
			// worker may touch canvas dimensions.
			canvasEl.width = Math.max(1, Math.round(metrics.cssWidth * metrics.dpr));
			canvasEl.height = Math.max(1, Math.round(metrics.cssHeight * metrics.dpr));
			handle.attachCanvas(
				{
					kind: 'landingDemo',
					width: metrics.cssWidth,
					height: metrics.cssHeight,
					dpr: metrics.dpr,
					extra: [metrics.materialGridSsaaScale]
				},
				canvasEl
			);
			isRegistered = true;
			logSurfaceSized(metrics);
			applyLandingConfig(handle);
			const darkMode = currentDark();
			// The worker owns the frame loop and anchors t=0 at its first frame,
			// matching the poster capture. ?poster renders exactly one frame
			// pinned to t=0 and does not loop.
			handle.demoLoop(
				'landingDemo',
				posterCaptureMode ? { darkMode, posterFrame: true } : { darkMode }
			);
			status = 'ready';
			logBootDuration('initEngine: commands issued', initStartedAt);
		} catch (error) {
			handleInitError(error);
		}
	}

	function onResize(): void {
		if (!isRegistered || !project) return;
		const metrics = surfaceMetrics();
		if (!metrics) return;
		if (
			metrics.cssWidth === lastCssWidth &&
			metrics.cssHeight === lastCssHeight &&
			metrics.dpr === lastDpr &&
			metrics.materialGridSsaaScale === lastMaterialGridSsaaScale
		)
			return;
		lastCssWidth = metrics.cssWidth;
		lastCssHeight = metrics.cssHeight;
		lastDpr = metrics.dpr;
		lastMaterialGridSsaaScale = metrics.materialGridSsaaScale;
		project.resizeSurface(
			'landingDemo',
			metrics.cssWidth,
			metrics.cssHeight,
			metrics.dpr,
			undefined,
			[metrics.materialGridSsaaScale]
		);
		logSurfaceSized(metrics);
	}

	onMount(() => {
		logLandingBoot('AqueductDemo: mounted', {
			hasWebGpu: Boolean(navigator.gpu),
			documentReadyState: document.readyState
		});
		logLandingResourceSummary('AqueductDemo: resource summary at mount');
		posterCaptureMode = new URLSearchParams(window.location.search).has('poster');
		if (navigator.gpu) startPrefetch();
		window.addEventListener('pointerdown', handleWindowPointerDown);
		void initEngine();

		resizeObserver = new ResizeObserver(() => onResize());
		resizeObserver.observe(containerEl);

		// The theme toggle flips the <html> class without any prop update here;
		// re-sending the loop config updates darkness without resetting the
		// worker's demo clock.
		themeObserver = new MutationObserver(() => {
			if (isRegistered && project && !posterCaptureMode) {
				project.demoLoop('landingDemo', { darkMode: currentDark() });
			}
		});
		themeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class']
		});

		return () => {
			window.removeEventListener('pointerdown', handleWindowPointerDown);
		};
	});

	onDestroy(() => {
		destroyed = true;
		resizeObserver?.disconnect();
		resizeObserver = null;
		themeObserver?.disconnect();
		themeObserver = null;
		unsubscribe?.();
		unsubscribe = null;
		session?.close();
		session = null;
		project = null;
		isRegistered = false;
	});
</script>

<div bind:this={containerEl} class="w-full relative">
	<div class="graph-area relative select-none">
		<canvas
			bind:this={canvasEl}
			class="aqueduct-landing-demo-canvas absolute inset-0 block h-full w-full cursor-grab touch-pan-y active:cursor-grabbing
					{status === 'ready' && !showPoster ? 'opacity-100' : 'opacity-0 pointer-events-none'}"
			onpointerdown={handlePointerDown}
			onpointermove={handlePointerMove}
			onpointerleave={handlePointerLeave}
			onpointerup={handlePointerUp}
			onpointercancel={handlePointerUp}
		></canvas>

		{#if showPoster && !posterCaptureMode}
			<!-- Engine-rendered t=0 posters (light/dark), shown from first
			     paint until the live canvas presents the same frame, at the
			     resolution the canvas will render (LandingPoster). -->
			<LandingPoster
				base="/landing/iliad-demo-poster-light"
				imgClass="pointer-events-none absolute inset-0 h-full w-full dark:hidden"
			/>
			<LandingPoster
				base="/landing/iliad-demo-poster-dark"
				imgClass="pointer-events-none absolute inset-0 hidden h-full w-full dark:block"
			/>
		{/if}

		{#if status === 'no-webgpu'}
			<div class="absolute inset-x-0 top-0 z-20">
				<WebGpuWarning
					description="This interactive demo requires WebGPU to render procedural textures on your GPU."
				/>
			</div>
		{/if}

		{#if LANDING_TUNER_VISIBLE}
			<div
				role="group"
				aria-label="Landing demo tuning controls"
				class="absolute right-2 top-2 z-50 grid w-[368px] max-w-[calc(100%-1rem)] gap-2 rounded border border-white/15 bg-zinc-950/95 p-2 text-[10px] leading-none text-zinc-300 shadow-xl backdrop-blur"
				onpointerdown={stopTunerPointer}
				onpointermove={stopTunerPointer}
				onpointerup={stopTunerPointer}
				onpointercancel={stopTunerPointer}
			>
				<div class="grid grid-cols-[1fr_auto] items-center gap-2">
					<div class="grid grid-cols-3 gap-1">
						<button
							type="button"
							class="h-5 rounded border px-1 font-medium {tunerTab === 'bloom'
								? 'border-cyan-300/60 bg-cyan-300/15 text-cyan-100'
								: 'border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-100'}"
							onclick={() => (tunerTab = 'bloom')}
						>
							Bloom
						</button>
						<button
							type="button"
							class="h-5 rounded border px-1 font-medium {tunerTab === 'edges'
								? 'border-cyan-300/60 bg-cyan-300/15 text-cyan-100'
								: 'border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-100'}"
							onclick={() => (tunerTab = 'edges')}
						>
							Edges
						</button>
						<button
							type="button"
							class="h-5 rounded border px-1 font-medium {tunerTab === 'particles'
								? 'border-cyan-300/60 bg-cyan-300/15 text-cyan-100'
								: 'border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-100'}"
							onclick={() => (tunerTab = 'particles')}
						>
							Particles
						</button>
					</div>
					<div class="flex items-center gap-1">
						<button
							type="button"
							class="h-5 rounded border border-white/15 px-1.5 text-[10px] font-medium text-zinc-300 hover:border-white/30 hover:text-white"
							onclick={resetTunerConfig}
						>
							Reset
						</button>
						<button
							type="button"
							class="h-5 rounded border border-white/15 px-1.5 text-[10px] font-medium text-zinc-300 hover:border-white/30 hover:text-white"
							onclick={() => (tunerExpanded = !tunerExpanded)}
						>
							{tunerExpanded ? 'Collapse' : 'Expand'}
						</button>
					</div>
				</div>

				{#if !tunerExpanded}
					<div class="text-[10px] text-zinc-500">
						{#if tunerTab === 'bloom'}
							Sigma {bloomSigmaCssPx.toFixed(2)} / Alpha {bloomOpacity.toFixed(3)}
						{:else if tunerTab === 'edges'}
							Alpha {edgeOpacity.toFixed(3)} / Falloff {edgeFalloff.toFixed(2)}
						{:else}
							Size {particleSize.toFixed(2)} / Group {particleGroupOpacity.toFixed(2)}
						{/if}
					</div>
				{:else if tunerTab === 'bloom'}
					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Sigma</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0"
							max="16"
							step="0.25"
							value={bloomSigmaCssPx}
							oninput={handleBloomSigmaInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0"
							max="16"
							step="0.25"
							value={bloomSigmaCssPx}
							oninput={handleBloomSigmaInput}
						/>
					</label>

					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Alpha</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0"
							max="1"
							step="0.025"
							value={bloomOpacity}
							oninput={handleBloomOpacityInput}
							onchange={handleBloomOpacityInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0"
							max="1"
							step="0.025"
							value={bloomOpacity}
							oninput={handleBloomOpacityInput}
							onchange={handleBloomOpacityInput}
						/>
					</label>
				{:else if tunerTab === 'edges'}
					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Alpha</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0"
							max="0.4"
							step="0.005"
							value={edgeOpacity}
							oninput={handleEdgeOpacityInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0"
							max="0.4"
							step="0.005"
							value={edgeOpacity}
							oninput={handleEdgeOpacityInput}
						/>
					</label>

					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Falloff</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0.05"
							max="8"
							step="0.05"
							value={edgeFalloff}
							oninput={handleEdgeFalloffInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0.05"
							max="8"
							step="0.05"
							value={edgeFalloff}
							oninput={handleEdgeFalloffInput}
						/>
					</label>

					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Bright</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0"
							max="4"
							step="0.025"
							value={edgeBrightness}
							oninput={handleEdgeBrightnessInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0"
							max="4"
							step="0.025"
							value={edgeBrightness}
							oninput={handleEdgeBrightnessInput}
						/>
					</label>
				{:else}
					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Size</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0.1"
							max="4"
							step="0.025"
							value={particleSize}
							oninput={handleParticleSizeInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0.1"
							max="4"
							step="0.025"
							value={particleSize}
							oninput={handleParticleSizeInput}
						/>
					</label>

					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Outer</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0"
							max="4"
							step="0.025"
							value={particleOuterOpacity}
							oninput={handleParticleOuterOpacityInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0"
							max="4"
							step="0.025"
							value={particleOuterOpacity}
							oninput={handleParticleOuterOpacityInput}
						/>
					</label>

					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Inner</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0"
							max="4"
							step="0.025"
							value={particleInnerOpacity}
							oninput={handleParticleInnerOpacityInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0"
							max="4"
							step="0.025"
							value={particleInnerOpacity}
							oninput={handleParticleInnerOpacityInput}
						/>
					</label>

					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Falloff</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0.1"
							max="8"
							step="0.05"
							value={particleColorFalloff}
							oninput={handleParticleColorFalloffInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0.1"
							max="8"
							step="0.05"
							value={particleColorFalloff}
							oninput={handleParticleColorFalloffInput}
						/>
					</label>

					<label class="grid grid-cols-[4.3rem_1fr_3.6rem] items-center gap-2">
						<span>Group</span>
						<input
							type="range"
							class="h-5 w-full"
							min="0"
							max="2"
							step="0.025"
							value={particleGroupOpacity}
							oninput={handleParticleGroupOpacityInput}
						/>
						<input
							type="number"
							class="h-5 rounded border border-white/15 bg-zinc-900 px-1 text-right tabular-nums text-zinc-100"
							min="0"
							max="2"
							step="0.025"
							value={particleGroupOpacity}
							oninput={handleParticleGroupOpacityInput}
						/>
					</label>
				{/if}
			</div>
		{/if}
	</div>
</div>

<style>
	.graph-area {
		aspect-ratio: 392 / 340;
		width: 100%;
	}
</style>
